import type { Completion } from '../utils/prompt-completion.js';

export interface Suggestion {
  /** What goes in the message after `/` or `@` */
  value: string;
  /** What the row shows */
  label: string;
  folder?: boolean;
}

/**
 * The suggestions above the prompt field (`/` commands, `@` files), as a listbox the field drives: the active row is
 * the field's aria-activedescendant, so focus stays in the field while arrows move.
 */
export function PromptSuggestions({ id, completion, items, active, onPick, onHover }: {
  id: string; completion: Completion; items: Suggestion[]; active: number;
  onPick: (s: Suggestion) => void; onHover: (i: number) => void;
}) {
  const mono = completion.kind === 'file';
  return (
    <ul id={id} role="listbox" aria-label={completion.kind === 'command' ? 'Commands and skills' : 'Files and folders'}
      className="absolute bottom-full left-0 right-0 z-20 mb-1 max-h-64 overflow-y-auto rounded-lg border py-1 text-sm shadow-lg"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
      {items.map((s, i) => (
        <li key={s.value} id={`${id}-${i}`} role="option" aria-selected={i === active}
          // Keep the field focused: the pick happens on click, the caret stays where it was
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(s)} onMouseEnter={() => onHover(i)}
          className="flex cursor-pointer items-center gap-2 px-3 py-1.5 max-sm:py-2.5"
          style={{
            backgroundColor: i === active ? 'var(--color-brand-dim)' : 'transparent',
            color: i === active ? 'var(--color-brand-light)' : 'var(--color-text)',
            fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          }}>
          <span aria-hidden="true" style={{ color: 'var(--color-text-3)' }}>{completion.kind === 'command' ? '/' : s.folder ? '▸' : '·'}</span>
          <span className="truncate">{s.label}{s.folder ? '/' : ''}</span>
        </li>
      ))}
    </ul>
  );
}
